import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { notifyIcon } from "../../../../config";
import { getNotification } from "../../../../utils/common";
import "./RuleForm.scss";
import { DELETE_ACTION_ID, defaultFormData, labels } from "./ruleEngine.constants";
import { prepareConditionsForUpdate, transformApiResponseToFormData, transformFormDataToApiPayload, validateForm } from "./ruleEngine.helper";
import { addEmailRules } from "./rules.service";

/**
 * Custom hook for form state and handlers
 * @param {Object} editingRule - Rule being edited, if any
 * @param {Object} dependencies - Context and other dependencies needed for handling form actions
 * @returns {Object} Form state and handlers
 * @author Sarthak Arora
 */
export const useRuleForm = (editingRule, dependencies) => {
  const { workflowId, handleUpdate, refreshRules, onClose } = dependencies;
  const { user } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState({});
  let isAddRuleClicked = false;

  // Initialize form with existing rule data or defaults
  useEffect(() => {
    if (editingRule) {
      setFormData(transformApiResponseToFormData(editingRule));
    } else {
      setFormData(defaultFormData);
    }
  }, [editingRule]);

  /**
   * Checks if a specific action is selected in the form
   * @author Sarthak Arora
   */
  const isActionSelected = (actionId) => {
    return formData.actions.split(",").includes(actionId.toString());
  };

  /**
   * Toggles an action's selection state and handles special cases like delete action
   * In case of delete action, other actions are removed
   * @author Sarthak Arora
   */
  const toggleAction = (actionId) => {
    const currentActions = formData.actions.split(",").map((id) => id.trim());
    const actionIdStr = actionId.toString();

    // If trying to add delete action, remove all other actions
    if (actionId === DELETE_ACTION_ID && !currentActions.includes(actionIdStr)) {
      setFormData((prev) => ({
        ...prev,
        actions: actionIdStr,
      }));
      return;
    }

    // If trying to add any other action while delete is selected, remove delete
    if (actionId !== DELETE_ACTION_ID && currentActions.includes(DELETE_ACTION_ID.toString())) {
      setFormData((prev) => ({
        ...prev,
        actions: actionIdStr,
      }));
      return;
    }

    let newActions;
    if (currentActions.includes(actionIdStr)) {
      newActions = currentActions.filter((id) => id !== actionIdStr);
    } else {
      newActions = [...currentActions, actionIdStr];
    }

    setFormData((prev) => ({
      ...prev,
      actions: newActions.join(","),
    }));
  };

  /**
   * Handles changes to location checkboxes and updates form state
   * @author Sarthak Arora
   */
  const handleLocationChange = (field) => {
    setFormData((prev) => ({
      ...prev,
      locations: {
        ...prev.locations,
        [field]: !prev.locations[field],
      },
    }));
  };

  /**
   * Handles changes to action checkboxes
   * @author Sarthak Arora
   */
  const handleActionChange = (actionId) => {
    toggleAction(actionId);
  };

  /**
   * Handles changes to condition fields
   * @author Sarthak Arora
   */
  const handleConditionChange = (index, field, value) => {
    setFormData((prev) => {
      const newConditions = [...prev.conditions];
      newConditions[index] = {
        ...newConditions[index],
        [field]: value,
      };
      return {
        ...prev,
        conditions: newConditions,
      };
    });
  };

  /**
   * Toggles the disabled state of a condition
   * @author Sarthak Arora
   */
  const handleConditionToggle = (index) => {
    setFormData((prev) => {
      const newConditions = [...prev.conditions];
      newConditions[index] = {
        ...newConditions[index],
        isDisabled: !newConditions[index].isDisabled,
      };
      return {
        ...prev,
        conditions: newConditions,
      };
    });
  };

  /**
   * Handles changes to the rule name input
   * @author Sarthak Arora
   */
  const nameChangeHandler = (e) => {
    setFormData((prev) => ({ ...prev, name: e.value }));
  };

  /**
   * Validates the form and updates the errors state
   * @returns {boolean} Whether the form is valid
   * @author Sarthak Arora
   */
  const validateFormAndUpdateErrors = () => {
    const newErrors = validateForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handles form submission for both new and existing rules
   * @author Sarthak Arora
   */
  const handleSubmit = async () => {
    isAddRuleClicked = true;
    if (validateFormAndUpdateErrors()) {
      try {
        if (editingRule) {
          const changedFields = {};

          if (formData.name !== editingRule.name) {
            changedFields.name = formData.name;
          }
          const formConditions = prepareConditionsForUpdate(formData, editingRule.conditions);

          if (formConditions.length > 0) {
            changedFields.conditions = formConditions;
          }

          const actionsChanged = formData.actions !== editingRule.actions;
          if (actionsChanged) {
            changedFields.actions = formData.actions;
          }

          const statusChanged = formData.isDisabled !== editingRule.isDisabled;
          if (statusChanged) {
            changedFields.isDisabled = formData.isDisabled;
          }

          changedFields.ruleId = editingRule.id;

          if (Object.keys(changedFields).length > 1) {
            await handleUpdate(changedFields);
          } else {
            onClose();
          }
        } else {
          const payload = transformFormDataToApiPayload(formData, workflowId, user);
          await addEmailRules(payload);
          refreshRules();
          onClose();
        }
      } catch (error) {
        getNotification(labels.NOTIFICATIONS.RULE_ADD_FAILURE, notifyIcon.ERROR_ICON);
      }
    }
  };

  // Validate form when data changes
  useEffect(() => {
    validateFormAndUpdateErrors();
  }, [formData]);

  return {
    formData,
    setFormData,
    errors,
    isActionSelected,
    handleLocationChange,
    handleActionChange,
    handleConditionChange,
    handleConditionToggle,
    nameChangeHandler,
    handleSubmit,
  };
};
